/** Formatting helpers. Fleet operators read these numbers all day, so the
 *  units are always explicit and the precision never implies more accuracy
 *  than a consumer GPS actually has. */

export function km(metres: number | undefined | null, digits = 1): string {
  if (metres == null) return '—'
  return `${(metres / 1000).toFixed(digits)} km`
}

export function kph(v: number | undefined | null): string {
  if (v == null) return '—'
  return `${Math.round(v)} km/h`
}

/** Durations read as "2h 14m", not "134 minutes" or "8040". */
export function duration(seconds: number | undefined | null): string {
  if (seconds == null || seconds < 0) return '—'
  const s = Math.floor(seconds)
  if (s < 60) return `${s}s`

  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`

  const h = Math.floor(m / 60)
  const rem = m % 60
  if (h < 24) return rem ? `${h}h ${rem}m` : `${h}h`

  const d = Math.floor(h / 24)
  return `${d}d ${h % 24}h`
}

export function minutes(mins: number | undefined | null): string {
  if (mins == null) return '—'
  return duration(mins * 60)
}

/** "4 min ago" / "just now". The live map leans on this heavily. */
export function ago(iso: string | undefined | null, now = Date.now()): string {
  if (!iso) return 'never'
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return '—'

  const diff = Math.round((now - t) / 1000)
  if (diff < 5) return 'just now'
  if (diff < 60) return `${diff}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`
  return dateOnly(iso)
}

export function clock(iso: string | undefined | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })
}

export function dateTime(iso: string | undefined | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

export function dateOnly(iso: string | undefined | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
}

export function money(cents: number | undefined | null, currency = 'USD'): string {
  if (cents == null) return '—'
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100)
}

export function count(n: number | undefined | null): string {
  if (n == null) return '—'
  return new Intl.NumberFormat().format(n)
}

export function pct(v: number | undefined | null, digits = 0): string {
  if (v == null) return '—'
  return `${v.toFixed(digits)}%`
}

export function bytes(n: number | undefined | null): string {
  if (n == null) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0
  let v = n
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

/** 173° → "S". Eight points is as precise as a heading readout usefully gets. */
export function compass(heading: number | undefined | null): string {
  if (heading == null) return '—'
  const points = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']
  return points[Math.round(((heading % 360) / 45)) % 8]
}

export function coords(lat?: number | null, lon?: number | null): string {
  if (lat == null || lon == null) return '—'
  return `${lat.toFixed(5)}, ${lon.toFixed(5)}`
}

/** Event type slugs come from the engine; the UI shows them as prose. */
const EVENT_LABELS: Record<string, string> = {
  ignition_on: 'Ignition on',
  ignition_off: 'Ignition off',
  overspeed: 'Speeding',
  geofence_enter: 'Entered geofence',
  geofence_exit: 'Left geofence',
  harsh_accel: 'Harsh acceleration',
  harsh_brake: 'Harsh braking',
  harsh_corner: 'Harsh cornering',
  idle_start: 'Started idling',
  idle_end: 'Stopped idling',
  trip_start: 'Trip started',
  trip_end: 'Trip ended',
  power_cut: 'Power disconnected',
  low_battery: 'Low backup battery',
  low_external_voltage: 'Low vehicle voltage',
  sos: 'Panic button',
  tow: 'Towing detected',
  offline: 'Lost signal',
  online: 'Back online',
  temperature_high: 'Temperature high',
  temperature_low: 'Temperature low',
  fuel_drop: 'Fuel level dropped',
  fuel_fill: 'Refuelled',
  driver_change: 'Driver changed',
  input_high: 'Input activated',
  input_low: 'Input deactivated',
  safety_alarm: 'Safety alarm',
}

export function eventLabel(type: string): string {
  return EVENT_LABELS[type] ?? sentence(type)
}

const ROLE_LABELS: Record<string, string> = {
  platform_owner: 'Platform owner',
  platform_support: 'Platform support',
  reseller_admin: 'Reseller admin',
  reseller_support: 'Reseller support',
  client_admin: 'Client admin',
  fleet_manager: 'Fleet manager',
  dispatcher: 'Dispatcher',
  viewer: 'Viewer',
  driver: 'Driver',
}

export function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? sentence(role)
}

export function sentence(slug: string): string {
  const s = slug.replace(/[_.]/g, ' ').trim()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Joins non-empty parts with a separator, so an absent registration does not
 *  leave a dangling bullet. */
export function join(parts: (string | undefined | null)[], sep = ' · '): string {
  return parts.filter((p): p is string => Boolean(p && p.trim())).join(sep)
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('')
}
